import {
  adminPromoCreateSchema,
  adminPromoUpdateSchema,
  type AdminPromoCampaignView,
  type AdminPromoCreateInput,
  type AdminPromoUpdateInput,
  type PromoPlanName,
} from '@steado/admin-contract/promotions';
import { format } from 'date-fns';

/**
 * The form holds strings, the way its inputs do; `toCreateBody` and
 * `toUpdateBody` turn them into the wire shape. An empty optional field is
 * sent as null, never as "".
 */
export interface PromotionFormValues {
  readonly code: string;
  readonly name: string;
  readonly discountLabel: string;
  readonly appliesTo: PromoPlanName;
  readonly appleOfferReference: string;
  readonly appleCustomCode: string;
  readonly playBasePlanId: string;
  readonly playOfferId: string;
  /** `YYYY-MM-DDTHH:mm` in the browser's zone, as `DateTimePicker` holds it. */
  readonly startsAt: string;
  readonly endsAt: string;
  readonly maxClaims: string;
  readonly newSubscribersOnly: boolean;
}

export const EMPTY_PROMOTION_FORM: PromotionFormValues = {
  code: '',
  name: '',
  discountLabel: '',
  appliesTo: 'annual',
  appleOfferReference: '',
  appleCustomCode: '',
  playBasePlanId: '',
  playOfferId: '',
  startsAt: '',
  endsAt: '',
  maxClaims: '',
  newSubscribersOnly: true,
};

/** The Play base plans Steado sells, offered as suggestions rather than a closed list. */
export const PLAY_BASE_PLAN_SUGGESTIONS = ['annual-autorenewing', 'monthly'] as const;

const PICKER_FORMAT = "yyyy-MM-dd'T'HH:mm";

export function formValuesOf(campaign: AdminPromoCampaignView): PromotionFormValues {
  return {
    code: campaign.code,
    name: campaign.name,
    discountLabel: campaign.discountLabel,
    appliesTo: campaign.appliesTo,
    appleOfferReference: campaign.appleOfferReference ?? '',
    appleCustomCode: campaign.appleCustomCode ?? '',
    playBasePlanId: campaign.playBasePlanId ?? '',
    playOfferId: campaign.playOfferId ?? '',
    startsAt: campaign.startsAt === null ? '' : format(new Date(campaign.startsAt), PICKER_FORMAT),
    endsAt: campaign.endsAt === null ? '' : format(new Date(campaign.endsAt), PICKER_FORMAT),
    maxClaims: campaign.maxClaims === null ? '' : String(campaign.maxClaims),
    newSubscribersOnly: campaign.newSubscribersOnly,
  };
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function instantOrNull(value: string): string | null {
  return value === '' ? null : new Date(value).toISOString();
}

function numberOrNull(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

type CampaignBody = Omit<AdminPromoCreateInput, 'code'>;

function campaignBody(values: PromotionFormValues): CampaignBody {
  return {
    name: values.name.trim(),
    discountLabel: values.discountLabel.trim(),
    appliesTo: values.appliesTo,
    appleOfferReference: textOrNull(values.appleOfferReference),
    appleCustomCode: textOrNull(values.appleCustomCode),
    playBasePlanId: textOrNull(values.playBasePlanId),
    playOfferId: textOrNull(values.playOfferId),
    startsAt: instantOrNull(values.startsAt),
    endsAt: instantOrNull(values.endsAt),
    maxClaims: numberOrNull(values.maxClaims),
    newSubscribersOnly: values.newSubscribersOnly,
  };
}

export function toCreateBody(values: PromotionFormValues): AdminPromoCreateInput {
  return { code: values.code.trim().toUpperCase(), ...campaignBody(values) };
}

/**
 * Only the fields the operator changed. Re-sending the rest would round the
 * dates to the picker's minute, and a store id sent back unchanged is still a
 * field the API has to compare against redemptions.
 */
export function toUpdateBody(
  values: PromotionFormValues,
  initial: PromotionFormValues,
): AdminPromoUpdateInput {
  const next = campaignBody(values);
  const before = campaignBody(initial);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next) as (keyof CampaignBody)[]) {
    if (next[key] !== before[key]) patch[key] = next[key];
  }
  return patch;
}

const FIELD_LABELS: Readonly<Record<string, string>> = {
  code: 'Code',
  name: 'Name',
  discountLabel: 'Discount label',
  appleOfferReference: 'Apple offer reference name',
  appleCustomCode: 'Apple custom code',
  playBasePlanId: 'Play base plan id',
  playOfferId: 'Play offer id',
  maxClaims: 'Max claims',
};

/**
 * What stands between the form and a save, as sentences. The contract schema
 * checks each field; the cross-field rules repeat what the API enforces, so
 * the operator finds out before saving rather than after.
 */
export function formIssues(values: PromotionFormValues, isNew: boolean): string[] {
  const body = campaignBody(values);
  const parsed = isNew
    ? adminPromoCreateSchema.safeParse({ code: values.code, ...body })
    : adminPromoUpdateSchema.safeParse(body);

  const issues: string[] = parsed.success
    ? []
    : parsed.error.issues.map((issue) => {
        const field = String(issue.path[0] ?? '');
        if (field === 'maxClaims') return 'Max claims must be a whole number of at least 1, or empty.';
        return `${FIELD_LABELS[field] ?? field}: ${issue.message}`;
      });

  const playIds = [body.playBasePlanId, body.playOfferId].filter((id) => id !== null).length;
  if (playIds === 1) issues.push('A Play offer needs both its base plan id and its offer id.');
  if (body.appleCustomCode === null && playIds === 0) {
    issues.push('Give the campaign an Apple custom code, a Play offer, or both.');
  }
  if (body.appleCustomCode !== null && body.appleOfferReference === null) {
    issues.push('An Apple custom code needs its offer reference name: it is how purchases are credited.');
  }
  if (body.startsAt !== null && body.endsAt !== null && body.endsAt <= body.startsAt) {
    issues.push('The campaign must end after it starts.');
  }

  return issues;
}
